import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Drawer, List, ListItem, ListItemIcon, ListItemText, Divider, Collapse, Box, Typography, Badge, useMediaQuery, ListItemButton, Avatar, Skeleton } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Header from './Header';
import menuItems from './menuItems';
import { FONT_FAMILY, LINK_COLOR, HOVER_BG, ACTIVE_BG, SIDEBAR_BG, SIDEBAR_BORDER } from '../Constant';
import LinearLoading from './reuse/LinearLoading';
import { useGetApiLoading } from '../hooks/useLoadingState';
import { useSelector } from 'react-redux';

function Layout({ children }) {
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width:900px)');
  const isGetApiLoading = useGetApiLoading();
  const { user } = useSelector((state) => state.auth);
  
  const avatarSrc = (user?.avatar || user?.image || user?.profileImage || user?.picture || '') || undefined;
  // No initials fallback; show a user icon when no image
  
  // Keep Settings dropdown open if any settings subpage is active
  useEffect(() => {
    if ([
      '/user-prompt',
      '/global-prompt-settings',
      '/terms-and-conditions',
    ].includes(location.pathname)) {
      setSettingsOpen(true);
    }
  }, [location.pathname]);

  // Keep Prompt dropdown open if any prompt subpage is active
  useEffect(() => {
    if ([
      '/user-prompt',
      '/global-prompt-settings',
    ].includes(location.pathname)) {
      setPromptOpen(true);
    }
  }, [location.pathname]);

  const handleSettingsClick = () => setSettingsOpen((open) => !open);
  const handlePromptClick = () => setPromptOpen((open) => !open);
  const handleDrawerToggle = () => setMobileOpen((open) => !open);

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Company Name with Icon */}
     
        <Box sx={{ 
          px: 2, 
          py: 2, 
          borderBottom: `1px solid ${SIDEBAR_BORDER}`,
          mb: 1 
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5 
          }}>
            <Avatar
              src={avatarSrc}
              alt={user?.name || user?.email || 'User'}
              sx={{
                width: 36,
                height: 36,
                bgcolor: '#000',
                color: '#fff',
                fontSize: 12,
                fontWeight: 700,
                flexShrink: 0
              }}
            >
              {!avatarSrc && (
                <PersonIcon sx={{ fontSize: 22, color: '#fff' }} />
              )}
            </Avatar>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  fontSize: 14, 
                  fontWeight: 700, 
                  color: '#000',
                  fontFamily: FONT_FAMILY,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  lineHeight: 1.2
                }}
              >
                Admin
              </Typography>

              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: 12, 
                  fontWeight: 500, 
                  color: '#666',
                  fontFamily: FONT_FAMILY,
                  // textTransform: 'uppercase',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'block',
                  maxWidth: '100%'
                }}
              >
                {user?.email ? user?.email : (
                  <Skeleton variant="text" width={140} height={16} />
                )}
              </Typography>
                 
            </Box>
          </Box>
        </Box>
   
      <List sx={{ fontSize: 14, fontFamily: FONT_FAMILY }}>
        {menuItems.map((item, idx) =>
          item.section ? (
            <Typography key={item.section + idx} sx={{ fontSize: 12, color: '#888', fontWeight: 500, px: 2, pt: 2, pb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: FONT_FAMILY }}>
              {item.section}
            </Typography>
          ) : (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                component={Link}
                to={item.path}
                sx={{
                  mb: 0.5,
                  fontSize: 14,
                  color: LINK_COLOR,
                  fontFamily: FONT_FAMILY,
                  background: location.pathname === item.path ? ACTIVE_BG : 'transparent',
                  '&:hover': {
                    background: HOVER_BG,
                  },
                  '&, & .MuiListItemIcon-root': {
                    color: LINK_COLOR,
                  },
                }}
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <ListItemIcon sx={{ color: LINK_COLOR, minWidth: 32 }}>{item.icon}</ListItemIcon>
                {item.badge ? (
                  <Badge badgeContent={item.badge} color="default" sx={{ '& .MuiBadge-badge': { fontSize: 10, minWidth: 18, height: 18, background: ACTIVE_BG, color: '#222', fontWeight: 600 } }}>
                    <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: 14, fontWeight: 500, fontFamily: FONT_FAMILY }} />
                  </Badge>
                ) : (
                  <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: 14, fontWeight: 500, fontFamily: FONT_FAMILY }} />
                )}
              </ListItemButton>
            </ListItem>
          )
        )}
      </List>
      <Box sx={{ flexGrow: 1 }} />
      <Divider sx={{ my: 1 }} />
      <List>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleSettingsClick}
            sx={{
              borderRadius: 0,
              color: LINK_COLOR,
              fontFamily: FONT_FAMILY,
              // Do not highlight the parent "Settings"; only highlight concrete child routes
              background: 'transparent',
              '&:hover': {
                background: HOVER_BG,
              },
              '&, & .MuiListItemIcon-root': {
                color: LINK_COLOR,
              },
            }}
          >
            <ListItemIcon sx={{ color: LINK_COLOR, minWidth: 32 }}><SettingsIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Settings" primaryTypographyProps={{ fontSize: 14, fontWeight: 500, fontFamily: FONT_FAMILY }} />
            {settingsOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
        </ListItem>
        <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {/* Prompt Dropdown */}
            <ListItem disablePadding>
              <ListItemButton
                onClick={handlePromptClick}
                sx={{
                  pl: 4,
                borderRadius: 0,
                  color: LINK_COLOR,
                  fontFamily: FONT_FAMILY,
                // Do not highlight the parent "Prompt"; only highlight its submenu routes
                background: 'transparent',
                  '&:hover': {
                    background: HOVER_BG,
                  },
                  '&, & .MuiListItemIcon-root': {
                    color: LINK_COLOR,
                  },
                }}
              >
                <ListItemText primary="Prompt" primaryTypographyProps={{ fontSize: 14, fontWeight: 500, fontFamily: FONT_FAMILY }} />
                {promptOpen ? <ExpandLess /> : <ExpandMore />}
              </ListItemButton>
            </ListItem>
            <Collapse in={promptOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
              
                <ListItem disablePadding>
                  <ListItemButton
                    sx={{
                      pl: 7,
                    borderRadius: 0,
                      color: LINK_COLOR,
                      fontFamily: FONT_FAMILY,
                      background: location.pathname === '/global-prompt-settings' ? ACTIVE_BG : 'transparent',
                      '&:hover': {
                        background: HOVER_BG,
                      },
                      '&, & .MuiListItemIcon-root': {
                        color: LINK_COLOR,
                      },
                    }}
                    component={Link}
                    to="/global-prompt-settings"
                    onClick={() => isMobile && setMobileOpen(false)}
                  >
                    <ListItemText primary="Global Prompt" primaryTypographyProps={{ fontSize: 14, fontWeight: 500, fontFamily: FONT_FAMILY }} />
                  </ListItemButton>
                </ListItem>
                  <ListItem disablePadding>
                  <ListItemButton
                    sx={{
                      pl: 7,
                    borderRadius: 0,
                      color: LINK_COLOR,
                      fontFamily: FONT_FAMILY,
                      background: location.pathname === '/user-prompt' ? ACTIVE_BG : 'transparent',
                      '&:hover': {
                        background: HOVER_BG,
                      },
                      '&, & .MuiListItemIcon-root': {
                        color: LINK_COLOR,
                      },
                    }}
                    component={Link}
                    to="/user-prompt"
                    onClick={() => isMobile && setMobileOpen(false)}
                  >
                    <ListItemText primary="User Prompt" primaryTypographyProps={{ fontSize: 14, fontWeight: 500, fontFamily: FONT_FAMILY }} />
                  </ListItemButton>
                </ListItem>
              </List>
            </Collapse>
            
            {/* Terms & Conditions */}
            <ListItem disablePadding>
              <ListItemButton
                sx={{
                  pl: 4,
                borderRadius: 0,
                  color: LINK_COLOR,
                  fontFamily: FONT_FAMILY,
                  background: location.pathname === '/terms-and-conditions' ? ACTIVE_BG : 'transparent',
                  '&:hover': {
                    background: HOVER_BG,
                  },
                  '&, & .MuiListItemIcon-root': {
                    color: LINK_COLOR,
                  },
                }}
                component={Link}
                to="/terms-and-conditions"
                onClick={() => isMobile && setMobileOpen(false)}
              >
                <ListItemText primary="Terms & Conditions" primaryTypographyProps={{ fontSize: 14, fontWeight: 500, fontFamily: FONT_FAMILY }} />
              </ListItemButton>
            </ListItem>
          </List>
        </Collapse>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: SIDEBAR_BG, fontFamily: FONT_FAMILY }}>
      {/* Global Linear Loading Bar for GET APIs */}
      <LinearLoading show={isGetApiLoading} />
      <Header onMenuClick={handleDrawerToggle} />
      {/* Sidebar */}
      <Box component="nav" sx={{ width: { sm: 220 }, flexShrink: { sm: 0 } }} aria-label="sidebar">
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              width: 220,
              boxSizing: 'border-box',
              background: SIDEBAR_BG,
              borderRight: `1px solid ${SIDEBAR_BORDER}`,
              pt: '70px',
              fontFamily: FONT_FAMILY,
            },
          }}
        >
          {drawerContent}
        </Drawer>
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              width: 220,
              boxSizing: 'border-box',
              background: SIDEBAR_BG,
              borderRight: `1px solid ${SIDEBAR_BORDER}`,
              pt: '70px',
              fontFamily: FONT_FAMILY,
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>
      {/* Main content */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', pt: 8, fontFamily: FONT_FAMILY, minWidth: 0, overflowX: 'hidden' }}>
        <Box sx={{ p: { xs: 1, sm: 4 }, width: '100%', minWidth: 0, mt: 0 }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}

export default Layout;
